import { useCallback, useEffect, useRef } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Close, CloudUpload, VideoOutline } from '@lyearn/icons';
import { ActionMenu, AddBlock } from '@lyearn/molecules';
import { useUppy } from '@lyearn/shared/hooks/useUppy';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import FileInput from '@/components/FileInput';
import { Image } from '@/components/Image';
import { adaptUploadMedia } from '@/features/messages/hooks/useAttachmentUpload';
import AddCoverDialog from '@/modules/corporate/components/AddCoverDialog';
import { CoverType } from '@/modules/corporate/components/ImageList/types';
import { MediaKindEnum } from '@/types/schema';

import { AddQuestionMediaProps, AddQuestionMediaType } from './types';
import { getQuestionMediaItems } from './utils';

const FILE_REFERENCE_ID = 'question-video';

const AddQuestionMedia: React.FC<AddQuestionMediaProps> = (props) => {
  const { media, onChange } = props;
  const { t } = useTranslation();

  const { uppy, files, isUploading, removeFileById } = useUppy({
    referenceId: FILE_REFERENCE_ID,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const [showCoverDialog, { toggle }] = useToggle(false);

  const items = getQuestionMediaItems(t);

  useEffect(() => {
    const filesValues = Object.values(files);
    if (isUploading || filesValues.length === 0) {
      return;
    }
    if (filesValues[0]?.url) {
      const file = adaptUploadMedia(filesValues[0] as any);
      onChange({ kind: MediaKindEnum.Video, url: file.url, name: file.name });
      removeFileById(file.mediaId);
    }
  }, [files, isUploading, onChange, removeFileById]);

  const onRemove = useCallback(
    (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      e.stopPropagation();
      onChange();
    },
    [onChange],
  );

  const onSubmit = useCallback(
    (selectedCard?: CoverType) => {
      if (selectedCard?.url) {
        onChange({
          kind: MediaKindEnum.Image,
          url: selectedCard?.url,
          name: selectedCard.name,
        });
      }
    },
    [onChange],
  );

  const onAction = useCallback(
    (actionType: AddQuestionMediaType) => {
      switch (actionType) {
        case AddQuestionMediaType.Image:
          toggle();
          break;
        case AddQuestionMediaType.Video:
          inputRef.current?.click();
          break;
        default:
          return null;
      }
    },
    [toggle],
  );

  return (
    <>
      <ActionMenu useAnchorElWidth disabled={isUploading} items={items} onAction={onAction}>
        {media?.url ? (
          <Stack
            alignItems="center"
            className={cn(
              'mb-4 cursor-pointer rounded-8 border border-divider p-8 hover:border-border-dark',
            )}
            justifyContent="between">
            <Stack alignItems="center" gapX={8}>
              {media.kind === MediaKindEnum.Video ? (
                <VideoOutline className="h-24 w-24" fillColorToken="icon-primary" />
              ) : (
                <Image
                  alt="question-media"
                  className="h-24 w-24 flex-shrink-0 rounded-4 border border-border"
                  height={24}
                  src={media.url}
                  width={24}
                />
              )}
              <Typography
                className="break-all"
                color="text-text-paragraph"
                numberOfLines={1}
                variant="body-short-02">
                {media?.name}
              </Typography>
            </Stack>
            <Close
              className="h-20 w-20 flex-shrink-0 text-icon-placeholder hover:text-icon-active"
              onClick={onRemove}
            />
          </Stack>
        ) : (
          <AddBlock
            className="group w-full"
            length="short"
            startIcon={<CloudUpload className="h-20 w-20 text-icon-placeholder" />}
            title={t('QuizCreator:QuestionsSettings.UploadAMediaFile')}
          />
        )}
      </ActionMenu>
      <FileInput
        ref={inputRef}
        accept="video/*"
        fileMeta={{ referenceId: FILE_REFERENCE_ID }}
        uppy={uppy}
      />
      {showCoverDialog ? (
        <AddCoverDialog
          cover={{ ...media, kind: MediaKindEnum.Image }}
          i18n={{ headerTitle: t('Common:Image.AddImage') }}
          onClose={toggle}
          onSubmit={onSubmit}
        />
      ) : null}
    </>
  );
};

export default AddQuestionMedia;
